import { Check, Copy, ExternalLink, Info, Palette } from 'lucide-react';
import React, { useState } from 'react';

import { useProviderCompany } from "../../../../context/ProviderCompanyContext";





const Branding = ({ providerCompanyId }) => {
    const { providerCompanyData } = useProviderCompany();
    const [copiedUrl, setCopiedUrl] = useState(null);

    const copyToClipboard = async (text, urlType) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedUrl(urlType);
            setTimeout(() => setCopiedUrl(null), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const LinkDisplay = ({ label, url, urlType, displayText, note }) => {
        if (!url) return null;
        
        return (
            <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
                <div className="mt-1 flex items-center gap-2 p-2 bg-gray-50 rounded-md border text-sm">
                    <a 
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-blue-600 hover:text-blue-800 font-medium"
                        title={url}
                    >
                        {displayText || url}
                    </a>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => copyToClipboard(url, urlType)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Copy link"
                        >
                            {copiedUrl === urlType ? (
                                <Check className="w-3 h-3 text-green-500" />
                            ) : (
                                <Copy className="w-3 h-3" />
                            )}
                        </button>
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Open in new tab"
                        >
                            <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                </div>
                {note && (
                    <p className="text-xs text-gray-500 mt-1 italic">{note}</p>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Information Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                    <Info className="text-blue-500 mr-3 mt-0.5" style={{ fontSize: "20px" }} />
                    <div>
                        <h4 className="text-sm font-medium text-blue-800 mb-1">Update Information</h4>
                        <p className="text-sm text-blue-700">
                            To update branding and legal information or other details, please contact us at{' '}
                            <a href="mailto:hello@officer.app" className="font-medium underline hover:text-blue-800">
                                hello@officer.app
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            {/* Branding Information Card */}
            <div className="flex items-start bg-white rounded-lg shadow-sm p-4">
                <Palette className="text-purple-500 mr-5 mt-1" style={{ fontSize: "20px" }} />
                <div className="flex-1">
                    <h6 className="mb-3 text-sm font-semibold text-brand-blue-dark">
                        Branding & Legal Information
                    </h6>

                    <div className="space-y-3">
                        <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Agency Name</span>
                            <p className="text-sm text-gray-900 mt-1">{providerCompanyData?.agency_name || 'Not specified'}</p>
                        </div>

                        <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Agency Address</span>
                            <p className="text-sm text-gray-900 mt-1">
                                {providerCompanyData?.agency_address || 'Not specified'}
                            </p>
                        </div>

                        {providerCompanyData?.agency_homepage_officer_app && (
                            <div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Officer.app Homepage</span>
                                <div className="mt-1 bg-white border border-gray-200 rounded-md overflow-hidden">
                                    <div className="flex items-center gap-2 p-2">
                                        <a 
                                            href={providerCompanyData.agency_homepage_officer_app}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                                            title={providerCompanyData.agency_homepage_officer_app}
                                        >
                                            View Your Public Profile
                                        </a>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => copyToClipboard(providerCompanyData.agency_homepage_officer_app, 'homepage')}
                                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                title="Copy link"
                                            >
                                                {copiedUrl === 'homepage' ? (
                                                    <Check className="w-3 h-3 text-green-500" />
                                                ) : (
                                                    <Copy className="w-3 h-3" />
                                                )}
                                            </button>
                                            <a
                                                href={providerCompanyData.agency_homepage_officer_app}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                title="Open in new tab"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                            </a>
                                        </div>
                                    </div>
                                    <div className="px-2 py-1 bg-blue-50 border-t border-blue-100">
                                        <p className="text-xs text-blue-700 flex items-center gap-1">
                                            💡 Share this link with the public to request officer details
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {providerCompanyData?.agency_services && providerCompanyData.agency_services.length > 0 && (
                            <div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Services Offered</span>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {providerCompanyData.agency_services.map((service, index) => (
                                        <span 
                                            key={index}
                                            className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md"
                                        >
                                            {service}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {providerCompanyData?.agency_office_hours_general && (
                            <div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">General Office Hours</span>
                                <p className="text-sm text-gray-900 mt-1">{providerCompanyData.agency_office_hours_general}</p>
                            </div>
                        )}

                        {providerCompanyData?.agency_office_hours_records && (
                            <div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Records Office Hours</span>
                                <p className="text-sm text-gray-900 mt-1">{providerCompanyData.agency_office_hours_records}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Branding;
